// Instagram isolated path - hook completo para gerenciar chats do Instagram
import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useQuery, useQueryClient, useMutation, keepPreviousData } from "@tanstack/react-query";
import { supabase, chatSupabase} from '@/integrations/supabase/client';
import {
  ChatInstagramWithMessages,
  ChatInstagram,
  ChatMessageInstagram,
  InstagramChatAdapter,
} from "@/types/instagramChat";
import { useOrganizationContext } from "@/contexts/OrganizationContext";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { buildOperatorMessagePayload, sendOperatorMessageToWebhook } from "@/utils/operatorMessageWebhook";
import { normalizePhone } from "@/utils/phoneUtils";

/** Retenta chamadas que falham por erro de rede (QUIC, Failed to fetch, etc.) */
const MAX_NETWORK_RETRIES = 3;
const NETWORK_RETRY_DELAY_MS = 1500;

function isNetworkError(err: unknown): boolean {
  const msg = err != null && typeof err === "object" && "message" in err
    ? String((err as { message?: string }).message)
    : String(err);
  return (
    msg.includes("Failed to fetch") ||
    msg.includes("ERR_QUIC") ||
    msg.includes("ERR_CONNECTION_CLOSED") ||
    msg.includes("connection closed") ||
    msg.includes("Load failed") ||
    msg.includes("NetworkError")
  );
}

async function withNetworkRetry<T>(fn: () => Promise<T>): Promise<T> {
  let lastErr: unknown;
  for (let attempt = 0; attempt <= MAX_NETWORK_RETRIES; attempt++) {
    try {
      return await fn();
    } catch (e) {
      lastErr = e;
      if (attempt < MAX_NETWORK_RETRIES && isNetworkError(e)) {
        await new Promise((r) => setTimeout(r, NETWORK_RETRY_DELAY_MS));
        continue;
      }
      throw e;
    }
  }
  throw lastErr;
}

export function useInstagramChats() {
  const [selectedInstance, setSelectedInstance] = useState<string | null>(null);
  const [selectedChat, setSelectedChat] = useState<ChatInstagramWithMessages | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAllMessages, setShowAllMessages] = useState(false); // Compatibilidade com Chats.tsx
  const { currentOrg } = useOrganizationContext();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const selectedChatIdsRef = useRef<{ chatId: string; instanceId: string } | null>(null);
  const prefetchTopRef = useRef<string>('');

  // Buscar instâncias: abertas (status=open) + quaisquer que tenham chats na org (para chats não sumirem)
  const { data: instances, isLoading: instancesLoading } = useQuery({
    queryKey: ["instances-unified-chats", currentOrg?.id],
    queryFn: async () => {
      if (!currentOrg?.id) {
        return [];
      }

      try {
        const { data: openData, error } = await supabase
          .from("instances")
          .select("*")
          .eq("org_id", currentOrg.id)
          .in("status", ["open", "not_validated"])
          .order("created_at", { ascending: false });

        if (error && !error.message?.includes("Failed to fetch")) {
          throw error;
        }
        const openList = openData || [];
        const openIds = new Set((openList as any[]).map((i: any) => i.id));

        // Incluir instâncias que tenham chats na org mas não estejam em "open" (ex.: status diferente)
        const { data: chatsMeta } = await chatSupabase
          .from('conversations')
          .select("instance_id")
          .eq("org_id", currentOrg.id);
        const chatInstanceIds = [...new Set((chatsMeta || []).map((r: any) => r.instance_id).filter(Boolean))] as string[];
        const missingIds = chatInstanceIds.filter((id) => !openIds.has(id));
        if (missingIds.length === 0) {
          return openList;
        }
        const { data: extraInstances } = await supabase
          .from("instances")
          .select("*")
          .eq("org_id", currentOrg.id)
          .in("id", missingIds);
        const combined = [...openList, ...(extraInstances || [])];
        return combined;
      } catch (err: any) {
        if (err?.message?.includes("Failed to fetch")) {
          console.warn("useInstagramChats: Erro de rede ao buscar instâncias");
          return [];
        }
        throw err;
      }
    },
    enabled: !!currentOrg?.id,
    retry: 2,
    retryDelay: 1000,
  });

  const instanceIds = useMemo(() => (instances || []).map((i: any) => i.id), [instances]);

  // Chave estável (sem instanceIds) para não trocar de cache ao refetch e evitar lista vazia após enviar mensagem
  const chatsQueryKey = ["chats-unified", currentOrg?.id] as const;
  // Buscar todos os chats da org de chats_Instagram e mensagens de chat_messages_instagram (todos os canais)
  const { data: chatsRaw, isLoading: chatsLoading, refetch: refetchChats, error: chatsError } = useQuery({
    queryKey: chatsQueryKey,
    enabled: !!currentOrg?.id && !instancesLoading && instanceIds.length > 0,
    queryFn: async () => {
      if (!currentOrg?.id || !instanceIds.length) {
        return [];
      }

      try {
        // 1–4. Buscar em paralelo: chats da org, preview de mensagens, instâncias e contatos
        const META_FIELDS = "chat_id, closed_at, user_closed_id, notes_closed, contact_id, origem_contato, status_atd, user_atribuido, momento_atribuido, instance_id, channel, created_at, atendimento_ia, protocolo, resumo_atd";
        const META_FIELDS_FALLBACK = "chat_id, closed_at, user_closed_id, notes_closed, status_atd, user_atribuido, momento_atribuido, instance_id, created_at, atendimento_ia";
        const chatsMetaPromise = (async (): Promise<any[]> => {
          const [openResult, recentResult] = await Promise.all([
            withNetworkRetry(() =>
              chatSupabase
                .from('conversations')
                .select(META_FIELDS)
                .eq("org_id", currentOrg.id)
                .is("closed_at", null)
            ),
            withNetworkRetry(() =>
              chatSupabase
                .from('conversations')
                .select(META_FIELDS)
                .eq("org_id", currentOrg.id)
                .not("closed_at", "is", null)
                .order("created_at", { ascending: false })
                .limit(1000)
            ),
          ]);

          const usesFallback = openResult.error?.code === "42703" || openResult.error?.message?.includes("does not exist");
          if (usesFallback) {
            const [openFb, recentFb] = await Promise.all([
              withNetworkRetry(() =>
                chatSupabase.from('conversations').select(META_FIELDS_FALLBACK).eq("org_id", currentOrg.id).is("closed_at", null)
              ),
              withNetworkRetry(() =>
                chatSupabase.from('conversations').select(META_FIELDS_FALLBACK).eq("org_id", currentOrg.id).not("closed_at", "is", null).order("created_at", { ascending: false }).limit(1000)
              ),
            ]);
            if (openFb.error) throw openFb.error;
            if (recentFb.error) throw recentFb.error;
            return [...(openFb.data || []), ...(recentFb.data || [])];
          }

          if (openResult.error) throw openResult.error;
          if (recentResult.error) throw recentResult.error;

          return [...(openResult.data || []), ...(recentResult.data || [])];
        })();

        const chatPreviewPromise = withNetworkRetry(() =>
          supabase.rpc("get_chat_list_preview", { p_instance_ids: instanceIds })
        );

        const instancesPromise = withNetworkRetry(() =>
          supabase.from("instances").select("id, channel").in("id", instanceIds)
        );

        const CONTACT_FIELDS = "id, org_id, name, email, whatsapp, phone, notes, created_at, updated_at, instance_id, visitor_id, Account_ID_insta, User_name_insta";
        const contactsPromise = queryClient.ensureQueryData({
          queryKey: ["contacts-unified", currentOrg.id],
          staleTime: 120_000,
          queryFn: async () => {
            const { data, error } = await withNetworkRetry(() =>
              supabase.from("contacts").select(CONTACT_FIELDS).eq("org_id", currentOrg.id)
            );
            if (error) throw error;
            return data || [];
          },
        });

        const [chatsMetaRows, { data: chatPreviews, error: messagesError }, { data: instancesChannelData }, contactsRows] = await Promise.all([
          chatsMetaPromise,
          chatPreviewPromise,
          instancesPromise,
          contactsPromise,
        ]);

        if (messagesError) {
          if (isNetworkError(messagesError)) {
            console.warn("[useInstagramChats] Erro de rede ao buscar preview de mensagens (pode ser temporário):", (messagesError as any)?.message);
          } else {
            console.error("[useInstagramChats] Erro ao buscar preview de mensagens:", messagesError);
          }
          // Não bloquear a lista inteira: metadados de conversations ainda carregam sem preview.
        }

        // 3. Construir chatMap a partir do preview agregado (uma entrada por (instance_id, chat_id))
        //    Substitui a query de 1000 mensagens flat pela função get_chat_list_preview.
        const chatMap = new Map<string, {
          chat_id: string;
          origem_contato: string;
          instance_id: string;
          lastMessage: any;
          unreadCount: number;
          messages: any[];
        }>();

        (chatPreviews || []).forEach((row: any) => {
          const key = `${row.instance_id}:${row.chat_id || ""}`;
          chatMap.set(key, {
            chat_id: row.chat_id ?? key,
            origem_contato: row.origem_contato ?? "",
            instance_id: row.instance_id,
            lastMessage: row.last_message_id ? {
              id: row.last_message_id,
              message_id: row.last_message_id,
              chat_id: row.chat_id,
              instance_id: row.instance_id,
              origem_contato: row.origem_contato,
              content_message: row.last_content_message,
              message_type: row.last_message_type,
              user_sender: row.last_user_sender,
              agent_id: row.last_agent_id,
              created_at: row.last_created_at,
              media_type: row.last_media_type,
              media_file_name: row.last_media_file_name,
              media_url: row.last_media_url,
              is_internal: row.last_is_internal,
              first_viewed_at: null,
            } : null,
            unreadCount: Number(row.unread_count ?? 0),
            messages: [],
          });
        });

        const chatsMetaMap = new Map<string, { chat_id: string | null; webchat_id: string | null; closed_at: string | null; user_closed_id: string | null; notes_closed: string | null; contact_id: string | null; origem_contato: string | null; status_atd: string | null; user_atribuido: string | null; momento_atribuido: string | null; instance_id: string | null; channel: string | null; created_at: string | null; atendimento_ia: string | null; protocolo: string | null; resumo_atd: string | null }>();
        chatsMetaRows.forEach((row: any) => {
          const cid = `${row.instance_id}:${(row.chat_id ?? row.origem_contato) || ""}`;
          const ch = (row.channel ?? "").toString().toLowerCase();
          chatsMetaMap.set(cid, {
            chat_id: row.chat_id ?? null,
            webchat_id: ch === "webchat" ? (row.chat_id ?? null) : null,
            closed_at: row.closed_at ?? null,
            user_closed_id: row.user_closed_id ?? null,
            notes_closed: row.notes_closed ?? null,
            contact_id: row.contact_id ?? null,
            origem_contato: row.origem_contato ?? null,
            status_atd: row.status_atd ?? null,
            user_atribuido: row.user_atribuido ?? null,
            momento_atribuido: row.momento_atribuido ?? null,
            instance_id: row.instance_id ?? null,
            protocolo: row.protocolo ?? null,
            resumo_atd: row.resumo_atd ?? null,
            channel: row.channel ?? null,
            created_at: row.created_at ?? null,
            atendimento_ia: row.atendimento_ia ?? null,
          });
        });

        // Canal da instância (já veio do Promise.all)
        const instanceChannelMap = new Map<string, string>();
        (instancesChannelData || []).forEach((inst: any) => {
          if (inst?.id && inst?.channel) instanceChannelMap.set(inst.id, String(inst.channel).toLowerCase());
        });

        const contactsData: any[] = contactsRows || [];

        const contactsMap = new Map<string, any>();
        contactsData.forEach((c: any) => {
          if (c.id) contactsMap.set(c.id, c);
          if (c.phone) {
            const ph = String(c.phone).trim();
            contactsMap.set(ph, c);
            const phNorm = normalizePhone(ph);
            if (phNorm && phNorm !== ph) contactsMap.set(phNorm, c);
          }
          if (c.whatsapp) {
            const wa = String(c.whatsapp).trim();
            contactsMap.set(wa, c);
            const waNorm = normalizePhone(wa);
            if (waNorm && waNorm !== wa) contactsMap.set(waNorm, c);
          }
          if (c.Account_ID_insta != null && String(c.Account_ID_insta).trim() !== "") contactsMap.set(String(c.Account_ID_insta).trim(), c);
        });

        // 4. Construir lista de chats: uma entrada por (instance_id, chat) para que mesmo cliente em canais diferentes apareça separado
        const seenChatIds = new Set<string>();
        const chatsWithMetadata: ChatInstagramWithMessages[] = [];

        for (const row of chatsMetaRows) {
          const cid = `${row.instance_id}:${(row.chat_id ?? row.origem_contato) || ""}`;
          if (seenChatIds.has(cid)) continue;
          seenChatIds.add(cid);
          const chatData = chatMap.get(cid) ?? {
            chat_id: row.chat_id ?? cid,
            origem_contato: row.origem_contato ?? "",
            instance_id: row.instance_id ?? "",
            lastMessage: null,
            unreadCount: 0,
            messages: [],
          };
          const meta = chatsMetaMap.get(cid);
          const origContato = String(chatData.origem_contato ?? "").trim();
          const contactById = meta?.contact_id ? contactsMap.get(meta.contact_id) : undefined;
          const contactByOrig = contactsMap.get(origContato)
            ?? contactsMap.get(origContato.replace(/\D/g, ""))
            ?? contactsMap.get(normalizePhone(origContato));
          const contact = contactById ?? contactByOrig;
          const displayName = contact?.name?.trim() ?? (String(chatData.origem_contato ?? "").trim() || null);
          // Canal vem de chats_Instagram.channel; todos usam chat_id (não existe coluna webchat_id).
          const instanceIdForChannel = row.instance_id ?? chatData.instance_id;
          const channelRaw = meta?.channel ?? row.channel ?? instanceChannelMap.get(instanceIdForChannel) ?? "instagram";
          const channel = channelRaw.toString().toLowerCase();
          const channelNorm = channel === "whatsapp" ? "whatsapp" : channel === "webchat" ? "webchat" : channel === "instagram" ? "instagram" : channel;
          chatsWithMetadata.push({
            chat_id: chatData.chat_id,
            webchat_id: channelNorm === "webchat" ? chatData.chat_id : null,
            contact_id: meta?.contact_id ?? chatData.origem_contato,
            org_id: currentOrg.id,
            instance_id: chatData.instance_id,
            channel: channelNorm as "instagram" | "whatsapp" | "webchat",
            origem_contato: chatData.origem_contato,
            atendimento_ia: meta?.atendimento_ia ?? null,
            status_atd: meta?.status_atd ?? null,
            user_atribuido: meta?.user_atribuido ?? null,
            momento_atribuido: meta?.momento_atribuido ?? null,
            closed_at: meta?.closed_at ?? null,
            user_closed_id: meta?.user_closed_id ?? null,
            notes_closed: meta?.notes_closed ?? null,
            protocolo: meta?.protocolo ?? row.protocolo ?? null,
            resumo_atd: meta?.resumo_atd ?? row.resumo_atd ?? null,
            created_at: meta?.created_at ?? chatData.lastMessage?.created_at ?? new Date().toISOString(),
            messages: chatData.messages as ChatMessageInstagram[],
            contact: contact ?? undefined,
            lastMessage: chatData.lastMessage,
            unreadCount: chatData.unreadCount,
            name: displayName,
          } as any);
        }

        // Mostrar apenas chats que têm linha em chats_Instagram (não incluir conversas só por terem mensagens em chat_messages_instagram)
        return chatsWithMetadata;
      } catch (err: any) {
        if (isNetworkError(err)) {
          console.warn("useInstagramChats: Erro de rede ao buscar chats (pode ser temporário):", err?.message);
        } else {
          console.error("useInstagramChats: Erro ao buscar chats:", {
            message: err?.message,
            code: err?.code,
            status: err?.status,
            details: err?.details,
            hint: err?.hint,
            fullError: err
          });
        }
        throw err;
      }
    },
    enabled: !!currentOrg?.id && instanceIds.length > 0,
    staleTime: 45_000,
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
    placeholderData: keepPreviousData,
  });

  // IDs do chat selecionado (para query direta de mensagens) + origem_contato para histórico completo
  const selectedChatIds = useMemo(() => {
    if (!selectedChat) return { chatId: null, instanceId: null, origemContato: null };
    const raw: any = selectedChat;
    // Priorizar o UUID real (full.chat_id) sobre raw.id que pode ser a chave composta "instance:chat_id"
    const chatId = raw.chat_id ?? raw.full?.chat_id ?? raw.id;
    const instanceId = raw.instance_id ?? raw.full?.instance_id ?? selectedInstance;
    const origemContato = raw.phone ?? raw.full?.origem_contato ?? raw.origem_contato ?? null;
    if (!instanceId) return { chatId: chatId ? String(chatId) : null, instanceId: null, origemContato: origemContato ? String(origemContato) : null };
    return { chatId: chatId ? String(chatId) : null, instanceId: String(instanceId), origemContato: origemContato ? String(origemContato) : null };
  }, [selectedChat, selectedInstance]);

  // Manter ref atualizada para o callback de realtime invalidar mensagens do chat aberto
  useEffect(() => {
    selectedChatIdsRef.current =
      selectedChatIds.chatId && selectedChatIds.instanceId
        ? { chatId: selectedChatIds.chatId, instanceId: selectedChatIds.instanceId }
        : null;
  }, [selectedChatIds.chatId, selectedChatIds.instanceId]);

  // Extrai o UUID real do chat_id, removendo prefixo "instance_id:" de chaves compostas
  const extractChatId = (chatId: string | null): string | null => {
    if (!chatId) return null;
    return chatId.includes(":") ? chatId.split(":").slice(1).join(":") : chatId;
  };

  // Per-chat Realtime subscription with server-side filter.
  // Fires only for messages in the currently open conversation — no client-side chat_id
  // comparison needed, no risk of missed events from format mismatches.
  useEffect(() => {
    const chatUuid = extractChatId(selectedChatIds.chatId);
    if (!chatUuid || !selectedChatIds.instanceId) return;

    const queryKey = [
      "chat-messages-instagram-direct",
      selectedChatIds.chatId,
      selectedChatIds.instanceId,
    ] as const;

    const handleMsg = (payload: any) => {
      const msg = payload.new ?? payload;
      if (msg?.message_id) {
        const normalized = { ...msg, id: msg.message_id ?? msg.id };
        queryClient.setQueriesData(
          { queryKey },
          (old: any[] | undefined) => {
            if (!old) return [normalized];
            const idx = old.findIndex(
              (m: any) => String(m.message_id) === String(normalized.message_id)
            );
            if (idx === -1) return [...old, normalized];
            const updated = [...old];
            updated[idx] = { ...old[idx], ...normalized };
            return updated;
          }
        );
      }
    };

    const channel = supabase
      .channel(`chat-msg-${chatUuid}`)
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "chat",
          table: "messages",
          filter: `chat_id=eq.${chatUuid}`,
        },
        handleMsg
      )
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "chat",
          table: "messages",
          filter: `chat_id=eq.${chatUuid}`,
        },
        handleMsg
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [selectedChatIds.chatId, selectedChatIds.instanceId, queryClient]);

  const { data: directMessages, isPending: isMessagesPending, isFetching: isMessagesFetching } = useQuery({
    queryKey: ["chat-messages-instagram-direct", selectedChatIds.chatId, selectedChatIds.instanceId, selectedChatIds.origemContato, showAllMessages],
    queryFn: async () => {
      if (!selectedChatIds.instanceId) return [];
      const realChatId = extractChatId(selectedChatIds.chatId);
      const pChatId = !showAllMessages && realChatId ? realChatId : null;
      const pOrigem = (showAllMessages || !realChatId) ? (selectedChatIds.origemContato ?? null) : null;
      if (!pChatId && !pOrigem) return [];
      const { data, error } = await supabase.rpc("get_chat_messages", {
        p_instance_id:    selectedChatIds.instanceId!,
        p_chat_id:        pChatId,
        p_origem_contato: pOrigem,
        p_limit:          showAllMessages ? 2000 : 100,
      });
      if (error) {
        console.warn("[useInstagramChats] Erro ao buscar mensagens do chat:", error.message);
        return [];
      }
      return (data || []).map((m: any) => ({ ...m, id: m.id ?? m.message_id }));
    },
    enabled: !!selectedChatIds.instanceId && (!!selectedChatIds.chatId || !!selectedChatIds.origemContato || showAllMessages),
    staleTime: 30_000,
    retry: 1,
    retryDelay: 2000,
    // WebSocket (subscription abaixo) invalida o cache a cada INSERT/UPDATE — polling desnecessário.
    refetchOnWindowFocus: false,
  });

  // Mensagens do chat selecionado: priorizar query direta, depois .full.messages e chatsRaw
  const selectedChatMessages = useMemo(() => {
    if (!selectedChat) {
      return [] as ChatMessageInstagram[];
    }

    // Query direta carregada: ordenar ascending
    if (Array.isArray(directMessages) && directMessages.length > 0) {
      return [...directMessages].sort(
        (a: any, b: any) => new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
      ) as ChatMessageInstagram[];
    }

    const rawSelected: any = selectedChat as any;
    // Priorizar UUID real (full.chat_id) sobre raw.id que pode ser chave composta "instance:chat_id"
    const chatId =
      rawSelected.chat_id ??
      rawSelected.full?.chat_id ??
      rawSelected.id;
    const chatIdStr = chatId != null ? String(chatId) : "";

    if (!chatIdStr) {
      return [] as ChatMessageInstagram[];
    }

    // Priorizar mensagens do próprio objeto do chat clicado (full.messages) para evitar perda ao mudar de chat
    const fullMessages = rawSelected.full?.messages;
    if (Array.isArray(fullMessages) && fullMessages.length > 0) {
      const sameChat =
        rawSelected.full?.chat_id != null &&
        String(rawSelected.full.chat_id) === chatIdStr;
      if (sameChat) {
        const sorted = [...fullMessages].sort((a, b) =>
          new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
        );
        return sorted.map((m: any) => ({
          ...m,
          id: m.id ?? m.message_id,
        })) as ChatMessageInstagram[];
      }
    }

    const sourceChats: any[] = (chatsRaw as any[]) || [];
    const selectedInstanceId = rawSelected.instance_id ?? rawSelected.full?.instance_id;
    let foundChat = sourceChats.find(
      (chat) => chat.chat_id != null && String(chat.chat_id) === chatIdStr && (!selectedInstanceId || chat.instance_id === selectedInstanceId)
    );
    if (!foundChat) {
      const phoneOrOrigem = rawSelected.phone ?? rawSelected.full?.origem_contato;
      if (phoneOrOrigem) {
        foundChat = sourceChats.find(
          (chat) => String(chat.origem_contato || "") === String(phoneOrOrigem) && (!selectedInstanceId || chat.instance_id === selectedInstanceId)
        );
      }
    }

    let messagesSource: ChatMessageInstagram[] =
      (foundChat?.messages as ChatMessageInstagram[]) ||
      (rawSelected.messages as ChatMessageInstagram[]) ||
      (rawSelected.full?.messages as ChatMessageInstagram[]) ||
      [];

    messagesSource = messagesSource.filter(
      (m: any) => !m.chat_id || String(m.chat_id) === chatIdStr
    );

    if (!messagesSource.length) {
      return [] as ChatMessageInstagram[];
    }

    const sortedMessages = [...messagesSource].sort((a, b) =>
      new Date(a.created_at || 0).getTime() - new Date(b.created_at || 0).getTime()
    );

    return sortedMessages.map((m: any) => ({
      ...m,
      id: m.id ?? m.message_id,
    })) as ChatMessageInstagram[];
  }, [selectedChat, chatsRaw, directMessages]);

  // Mutation: Nota interna = só insere em chat_messages_instagram. Mensagem ao cliente = só chama webhook (sem salvamento nem atualização de chat).
  const insertMessageMutation = useMutation({
    mutationFn: async (payload: {
      chat_id: string;
      instance_id: string;
      origem_contato: string;
      content_message: string;
      message_type: "text" | "internal" | "media";
      is_internal: boolean;
      media_type?: string | null;
      media_file_name?: string | null;
      media_url?: string | null;
      operator_name?: string | null;
    }) => {
      const now = new Date().toISOString();
      const operatorLabel = payload.operator_name?.trim() || "Operador";

      // Nota interna: apenas salvar no banco (mantém funcionalidade)
      if (payload.is_internal) {
        const row = {
          chat_id: payload.chat_id,
          instance_id: payload.instance_id,
          origem_contato: payload.origem_contato,
          content_message: payload.content_message,
          message_type: payload.message_type,
          is_internal: true,
          user_sender: operatorLabel,
          media_type: payload.media_type || null,
          media_file_name: payload.media_file_name || null,
          media_url: payload.media_url || null,
          created_at: now,
        };
        const { data, error } = await chatSupabase.from('messages').insert(row);
        if (error) throw error;
        return data;
      }

      // Mensagem ao cliente: não salvar, não atualizar chats_Instagram; apenas chamar webhook com todas as informações
      const fullChat = (selectedChat as any)?.full ?? selectedChat;
      const instance = (instances || []).find((i: any) => i.id === payload.instance_id);
      const contact = (selectedChat as any)?.contact ?? fullChat?.contact ?? null;

      // chat_id é o identificador único em todos os canais; webchat_id no payload = chat_id quando canal é webchat
      // Incluir channel, contact_id e todos os campos de atendimento para o webhook (status_atd, protocolo, etc.)
      const chatChannel = (fullChat as any)?.channel ?? (instance as any)?.channel ?? null;
      const contactId = (fullChat as any)?.contact_id ?? (contact && typeof contact === "object" && (contact as any).id) ?? null;
      const chatInfo: Record<string, unknown> = {
        chat_id: payload.chat_id,
        instance_id: payload.instance_id,
        origem_contato: payload.origem_contato,
        channel: chatChannel,
        contact_id: contactId,
        // Campos de atendimento (chats_Instagram)
        status_atd: (fullChat as any)?.status_atd ?? null,
        atendimento_ia: (fullChat as any)?.atendimento_ia ?? null,
        user_atribuido: (fullChat as any)?.user_atribuido ?? null,
        momento_atribuido: (fullChat as any)?.momento_atribuido ?? null,
        closed_at: (fullChat as any)?.closed_at ?? null,
        user_closed_id: (fullChat as any)?.user_closed_id ?? null,
        notes_closed: (fullChat as any)?.notes_closed ?? null,
        created_at: (fullChat as any)?.created_at ?? null,
        protocolo: (fullChat as any)?.protocolo ?? null,
        resumo_atd: (fullChat as any)?.resumo_atd ?? null,
        org_id: (fullChat as any)?.org_id ?? currentOrg?.id ?? null,
        ...(((fullChat as any)?.channel === "webchat" && (fullChat as any)?.chat_id) ? { webchat_id: (fullChat as any).chat_id } : {}),
      };
      const contactInfo: Record<string, unknown> = contact && typeof contact === "object"
        ? {
            id: (contact as any).id ?? contactId ?? null,
            contact_id: (contact as any).id ?? contactId ?? null,
            name: (contact as any).name ?? null,
            user_name_insta: (contact as any).user_name_insta ?? (contact as any).User_name_insta ?? null,
            phone: (contact as any).phone ?? payload.origem_contato ?? null,
            email: (contact as any).email ?? null,
            origem_contato: payload.origem_contato,
          }
        : { origem_contato: payload.origem_contato, contact_id: contactId };

      const webhookPayload = buildOperatorMessagePayload({
        userId: user?.id ?? null,
        userEmail: (user as any)?.email ?? null,
        userName: operatorLabel,
        orgId: currentOrg?.id,
        orgName: (currentOrg as any)?.name ?? null,
        instance: instance
          ? { id: instance.id, name: (instance as any).name, channel: chatChannel ?? (instance as any).channel, status: (instance as any).status }
          : { id: payload.instance_id, name: null, channel: chatChannel, status: null },
        chat: chatInfo,
        contact: contactInfo,
        message: {
          content_message: payload.content_message,
          message_type: payload.message_type,
          media_type: payload.media_type ?? null,
          media_file_name: payload.media_file_name ?? null,
          media_url: payload.media_url ?? null,
          created_at: now,
        },
      });
      const webhookResponse = await sendOperatorMessageToWebhook(webhookPayload, { supabase }).catch((err) => {
        console.warn("[useInstagramChats] Webhook mensagem ao cliente falhou:", err);
        return null;
      });

      return webhookResponse ?? null;
    },
    onSuccess: (_, variables) => {
      // Só atualiza a página quando for nota interna (mensagem ao cliente já atualizou no retorno do webhook)
      if (variables.is_internal) {
        const chatId = (selectedChat as any)?.chat_id ?? (selectedChat as any)?.id ?? (selectedChat as any)?.full?.chat_id;
        const instanceId = (selectedChat as any)?.instance_id ?? (selectedChat as any)?.full?.instance_id ?? selectedInstance;
        if (chatId && instanceId) {
          queryClient.invalidateQueries({
            queryKey: ["chat-messages-instagram-direct", chatId, instanceId],
          });
        }
        queryClient.invalidateQueries({
          queryKey: chatsQueryKey,
        });
        refetchChats();
      }
    },
  });

  // Helper: determina se uma mensagem é do cliente (não do operador/bot)
  const isClientMessage = (userSender: string | null | undefined, origemContato: string | null | undefined): boolean => {
    const sender = String(userSender ?? "").trim();
    const origem = String(origemContato ?? "").trim();
    return !sender || sender === "Cliente" || (origem !== "" && sender === origem);
  };

  // Mutation: Marcar mensagens do cliente como lidas (first_viewed_at) — sempre pelo chat_id real
  const markAsViewedMutation = useMutation({
    mutationFn: async (chatId: string) => {
      if (!chatId) return;
      const now = new Date().toISOString();

      // Incluir user_sender === origem_contato (Telegram/WhatsApp enviam o ID do contato como user_sender)
      const raw = selectedChat as any;
      const origemContato = String(raw?.full?.origem_contato ?? raw?.phone ?? raw?.origem_contato ?? "").trim();

      const senderFilters = ["user_sender.eq.Cliente", "user_sender.is.null", "user_sender.eq."];
      if (origemContato) senderFilters.push(`user_sender.eq.${origemContato}`);

      const { error } = await chatSupabase
        .from('messages')
        .update({ first_viewed_at: now })
        .eq("chat_id", chatId)
        .or(senderFilters.join(","))
        .is("first_viewed_at", null);

      if (error) throw error;
    },
    onSuccess: () => {
      const raw = selectedChat as any;
      const chatId = raw?.full?.chat_id ?? raw?.chat_id;
      const instanceId = raw?.full?.instance_id ?? raw?.instance_id;
      const origemContato = String(raw?.full?.origem_contato ?? raw?.phone ?? raw?.origem_contato ?? "").trim();
      const now = new Date().toISOString();

      if (chatId && instanceId) {
        // Atualizar cache das mensagens diretas: marcar first_viewed_at sem refetch
        queryClient.setQueryData(
          ["chat-messages-instagram-direct", chatId, instanceId, selectedChatIds.origemContato, showAllMessages],
          (old: any[] | undefined) => {
            if (!Array.isArray(old)) return old;
            return old.map((m: any) => {
              if (!m.first_viewed_at && isClientMessage(m.user_sender, origemContato || m.origem_contato)) {
                return { ...m, first_viewed_at: now };
              }
              return m;
            });
          }
        );
      }

      // Zerar unreadCount do chat na lista (chatsRaw) imediatamente
      if (chatId) {
        queryClient.setQueryData(chatsQueryKey, (old: any[] | undefined) => {
          if (!Array.isArray(old)) return old;
          return old.map((chat: any) =>
            chat.chat_id === chatId ? { ...chat, unreadCount: 0 } : chat
          );
        });
      }
    },
  });

  // Mutation: Encerrar chat (upsert em chats_Instagram para criar linha se não existir)
  const closeChatMutation = useMutation({
    mutationFn: async (payload: {
      chatId: string;
      userClosedId: string;
      notesClosed: string;
      instanceId?: string;
      orgId?: string;
      origemContato?: string;
    }) => {
      const now = new Date().toISOString();
      const row = {
        chat_id: payload.chatId,
        closed_at: now,
        user_closed_id: payload.userClosedId,
        notes_closed: payload.notesClosed?.trim() || null,
        instance_id: payload.instanceId ?? null,
        org_id: payload.orgId ?? null,
        origem_contato: payload.origemContato ?? null,
      } as any;
      const { error } = await chatSupabase
        .from('conversations')
        .upsert(row, { onConflict: "chat_id" });

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: chatsQueryKey,
      });
      // Não zerar selectedChat aqui: o refetch vai atualizar filteredChats com closed_at,
      // e o useEffect de sync vai propagar para selectedChat, exibindo o bloco de encerramento.
    },
    onError: (err: any) => {
      console.error("[useInstagramChats] Erro ao encerrar chat:", err);
      toast.error(err?.message || "Não foi possível encerrar o chat. Verifique permissões (RLS) ou o nome da tabela.");
    },
  });

  // Filtrar por instância selecionada (opcional). null = "Todas as instâncias" (mostra todos os chats)
  const filteredChats = useMemo(() => {
    let list = chatsRaw || [];
    if (selectedInstance != null && selectedInstance !== "") {
      list = list.filter((c: any) => c.instance_id === selectedInstance);
    }
    if (!searchQuery.trim()) return list;
    const searchLower = searchQuery.toLowerCase();
    return list.filter((chat: any) => {
      const contactName = chat.contact?.name || "";
      const contactHandle = chat.contact?.user_name_insta || chat.contact?.User_name_insta || "";
      const phone = chat.origem_contato || chat.phone || "";
      return (
        contactName.toLowerCase().includes(searchLower) ||
        contactHandle.toLowerCase().includes(searchLower) ||
        phone.toLowerCase().includes(searchLower)
      );
    });
  }, [chatsRaw, selectedInstance, searchQuery]);

  // Não auto-selecionar instância: deixar "Todas as instâncias" (null) para que todos os chats da org apareçam
  // O usuário pode filtrar por instância depois se quiser

  // Realtime: atualizar lista e mensagens (debounce + grace period após mount para evitar refetch ao atualizar página)
  const realtimeDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const realtimeRefreshInFlightRef = useRef(false);
  const mountTimeRef = useRef<number>(Date.now());
  useEffect(() => {
    mountTimeRef.current = Date.now();
  }, [currentOrg?.id, instanceIds.join(",")]);

  useEffect(() => {
    if (!currentOrg?.id || !instanceIds.length) return;

    // Upsert a message directly into the React Query cache without any network refetch.
    // Handles both INSERT (new message → append) and UPDATE (upsert/reaction/receipt → patch in place).
    // Only touches the query for the currently open chat — avoids scroll reset / flash.
    const upsertMessageInCache = (newMsg: any) => {
      const ids = selectedChatIdsRef.current;
      if (!ids?.chatId) return;

      // chatId may carry an "instance:" prefix — extract the UUID part
      const currentUuid = ids.chatId.includes(":")
        ? ids.chatId.split(":").slice(1).join(":")
        : ids.chatId;

      const msgChatId = newMsg.chat_id ? String(newMsg.chat_id) : null;
      if (!msgChatId || msgChatId !== currentUuid) return;

      const normalized = { ...newMsg, id: newMsg.message_id ?? newMsg.id };

      queryClient.setQueriesData(
        { queryKey: ["chat-messages-instagram-direct", ids.chatId, ids.instanceId] },
        (old: any[] | undefined) => {
          if (!old) return [normalized];
          const idx = old.findIndex((m: any) => String(m.message_id) === String(normalized.message_id));
          if (idx === -1) return [...old, normalized];          // INSERT: append
          const updated = [...old];
          updated[idx] = { ...old[idx], ...normalized };       // UPDATE: patch in place
          return updated;
        }
      );
    };

    const GRACE_MS = 3000;
    const DEBOUNCE_MS = 1200;

    // Atualiza lastMessage e unreadCount do chat afetado diretamente no cache,
    // sem esperar o refetch completo (evita latência de 1-2s na lista de chats).
    const updateLastMessageInCache = (msg: any) => {
      if (!msg?.chat_id) return;
      queryClient.setQueryData(chatsQueryKey, (old: any[] | undefined) => {
        if (!Array.isArray(old)) return old;
        return old.map((chat: any) => {
          if (String(chat.chat_id) !== String(msg.chat_id)) return chat;
          const isClient = !msg.user_sender || msg.user_sender === '' || msg.user_sender === 'Cliente' || msg.user_sender === msg.origem_contato;
          return {
            ...chat,
            lastMessage: { ...msg, id: msg.message_id ?? msg.id },
            unreadCount: isClient ? (chat.unreadCount || 0) + 1 : chat.unreadCount,
          };
        });
      });
    };

    // Debounced update of the chats list (last-message preview, unread counts).
    // Does NOT touch the open chat's messages query — that is handled by upsertMessageInCache.
    const scheduleChatsInvalidate = () => {
      if (Date.now() - mountTimeRef.current < GRACE_MS) return;
      if (realtimeDebounceRef.current) clearTimeout(realtimeDebounceRef.current);
      realtimeDebounceRef.current = setTimeout(() => {
        realtimeDebounceRef.current = null;
        if (realtimeRefreshInFlightRef.current) return;

        realtimeRefreshInFlightRef.current = true;
        void (async () => {
          try {
            await queryClient.cancelQueries({ queryKey: chatsQueryKey });
            await queryClient.invalidateQueries({
              queryKey: chatsQueryKey,
              refetchType: "active",
            });
          } finally {
            realtimeRefreshInFlightRef.current = false;
          }
        })();
      }, DEBOUNCE_MS);
    };

    // Org-wide subscription: atualiza lastMessage na lista imediatamente e agenda refetch completo.
    // Per-chat subscription acima lida com as mensagens do chat aberto.
    const channelMessages = supabase
      .channel(`unified-messages-${currentOrg.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "chat", table: "messages" },
        (payload: any) => {
          // INSERT: atualiza lastMessage no cache imediatamente, sem aguardar debounce
          if (payload.eventType === 'INSERT' && payload.new?.chat_id) {
            updateLastMessageInCache(payload.new);
          }
          scheduleChatsInvalidate();
        }
      )
      .subscribe();

    const channelChatsMeta = supabase
      .channel(`unified-chats-meta-${currentOrg.id}`)
      .on(
        "postgres_changes",
        { event: "*", schema: "chat", table: "conversations" },
        () => scheduleChatsInvalidate()
      )
      .subscribe();

    return () => {
      if (realtimeDebounceRef.current) {
        clearTimeout(realtimeDebounceRef.current);
        realtimeDebounceRef.current = null;
      }
      supabase.removeChannel(channelMessages);
      supabase.removeChannel(channelChatsMeta);
    };
  }, [currentOrg?.id, instanceIds, queryClient, chatsQueryKey]);

  // Sincronizar selectedChat quando a lista de chats for atualizada (realtime/refetch), para o header e UI refletirem user_atribuido, closed_at, etc.
  useEffect(() => {
    if (!selectedChat || !filteredChats.length) return;
    const rawSelected: any = selectedChat;
    const currentInstanceId = rawSelected.instance_id ?? rawSelected.full?.instance_id;

    // chat_id direto tem precedência; se não existir, extrai do id composto (formato instanceId:chatId)
    const directChatId: string | null = rawSelected.chat_id ?? rawSelected.full?.chat_id ?? null;
    const listId: string | null = typeof rawSelected.id === 'string' ? rawSelected.id : null;
    const extractedChatId = !directChatId && listId?.includes(':')
      ? listId.split(':').slice(1).join(':')
      : null;
    const effectiveChatId = directChatId || extractedChatId;

    const found = filteredChats.find((c: any) => {
      if (effectiveChatId && String(c.chat_id) === String(effectiveChatId)) return true;
      if (listId && `${c.instance_id}:${c.chat_id}` === listId) return true;
      return false;
    });
    if (!found) return;

    // Comparar campos significativos para evitar loop infinito:
    // O check por referência (rawSelectedFull === found) sempre falha após refetch (novo objeto),
    // causando setSelectedChat → markAsViewedMutation → invalidate → refetch → loop.
    const prevFull = rawSelected.full ?? rawSelected;
    const noChange =
      prevFull?.status_atd === found.status_atd &&
      prevFull?.user_atribuido === (found as any).user_atribuido &&
      prevFull?.closed_at === found.closed_at &&
      prevFull?.atendimento_ia === found.atendimento_ia &&
      prevFull?.user_closed_id === (found as any).user_closed_id &&
      prevFull?.notes_closed === (found as any).notes_closed &&
      prevFull?.unreadCount === found.unreadCount &&
      prevFull?.lastMessage?.created_at === found.lastMessage?.created_at;
    if (noChange) return;

    const ch = (found as any).channel ?? "instagram";
    const updatedListId = `${found.instance_id}:${found.chat_id}`;
    const updatedAdapter: ChatInstagramWithMessages = {
      id: updatedListId,
      phone: found.origem_contato,
      contact_key: `${ch}:${found.origem_contato}`,
      normalizedPhone: found.origem_contato,
      contactId: found.contact_id,
      contactName: found.contact?.name ?? found.name ?? null,
      contactHandle: found.contact?.user_name_insta ?? found.contact?.User_name_insta ?? null,
      instance_id: found.instance_id,
      channel: ch,
      status_atd: found.status_atd,
      atendimento_ia: found.atendimento_ia,
      user_atribuido: (found as any).user_atribuido ?? null,
      momento_atribuido: (found as any).momento_atribuido ?? null,
      closed_at: found.closed_at,
      user_closed_id: (found as any).user_closed_id ?? null,
      notes_closed: (found as any).notes_closed ?? null,
      created_at: found.created_at,
      unreadCount: found.unreadCount,
      lastMessage: found.lastMessage ?? null,
      lastMessageAt: found.lastMessage?.created_at ?? null,
      full: found,
    } as any;
    setSelectedChat(updatedAdapter);
  }, [filteredChats, selectedChat]);

  // Marcar mensagens do cliente como vistas sempre que o chat estiver aberto e houver não lidas
  // Dispara ao selecionar o chat E quando novas mensagens chegam (directMessages atualiza)
  useEffect(() => {
    if (!selectedChat || !user?.id) return;

    const rawSelected: any = selectedChat as any;
    const chatId = rawSelected?.full?.chat_id ?? rawSelected?.chat_id;
    if (!chatId) return;

    // Verificar se há mensagens do cliente não lidas antes de disparar o update
    if (Array.isArray(directMessages) && directMessages.length > 0) {
      const hasUnread = directMessages.some(
        (m: any) => !m.first_viewed_at && isClientMessage(m.user_sender, m.origem_contato)
      );
      if (!hasUnread) return;
    }

    markAsViewedMutation.mutate(chatId);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChat?.id ?? (selectedChat as any)?.full?.chat_id, directMessages, user?.id]);

  // Adaptar dados para compatibilidade com UI existente (channel vem de chats_Instagram). id único por (instance, chat) para mesmo cliente em canais diferentes.
  const adaptedChats: InstagramChatAdapter[] = filteredChats.map((chat: any) => {
    const ch = (chat.channel ?? "instagram").toString().toLowerCase();
    // Garantir identificador para lista: origem_contato, phone ou contact_id (evita clientes sumirem por falta de phone)
    const phoneOrKey = chat.origem_contato ?? chat.phone ?? chat.contact_id ?? "";
    const listId = `${chat.instance_id}:${chat.chat_id}`;
    return {
    id: listId,
    phone: phoneOrKey,
    contact_key: `${ch}:${phoneOrKey}`,
    normalizedPhone: phoneOrKey,
    contactId: chat.contact_id,
    contactName: chat.contact?.name ?? chat.name ?? null,
    contactHandle:
      chat.contact?.user_name_insta || chat.contact?.User_name_insta || null,
    instance_id: chat.instance_id,
    channel: ch,
    status_atd: chat.status_atd,
    atendimento_ia: chat.atendimento_ia,
    user_atribuido: (chat as any).user_atribuido ?? null,
    momento_atribuido: (chat as any).momento_atribuido ?? null,
    closed_at: chat.closed_at,
    user_closed_id: (chat as any).user_closed_id ?? null,
    notes_closed: (chat as any).notes_closed ?? null,
    created_at: chat.created_at,
    unreadCount: chat.unreadCount,
    lastMessage: chat.lastMessage || null,
    lastMessageAt: chat.lastMessage?.created_at || null,
    full: chat,
  };
  });

  // Pré-carrega mensagens de um chat ao hover — elimina latência percebida no clique
  const prefetchChat = useCallback((chat: any) => {
    const chatId   = chat.chat_id   ? String(chat.chat_id)   : null;
    const instId   = chat.instance_id ? String(chat.instance_id) : null;
    const origem   = chat.phone ?? chat.origem_contato ?? null;

    if (!instId || (!chatId && !origem)) return;

    const qKey = ["chat-messages-instagram-direct", chatId, instId, origem ? String(origem) : null, false];

    // Não prefetch se os dados ainda estão frescos (< 10s)
    const state = queryClient.getQueryState(qKey);
    if (state && Date.now() - (state.dataUpdatedAt || 0) < 10_000) return;

    const realChatId = extractChatId(chatId);

    queryClient.prefetchQuery({
      queryKey: qKey,
      queryFn: async () => {
        const { data, error } = await withNetworkRetry(() =>
          supabase.rpc("get_chat_messages", {
            p_instance_id:    instId,
            p_chat_id:        realChatId ?? null,
            p_origem_contato: realChatId ? null : (origem ?? null),
            p_limit:          20,
          })
        );
        if (error) return [];
        return (data || []).map((m: any) => ({ ...m, id: m.id ?? m.message_id }));
      },
      staleTime: 10_000,
    });
  }, [queryClient]);

  // Auto-prefetch dos 5 primeiros chats da lista ao carregar — esconde latência antes do clique
  useEffect(() => {
    if (!adaptedChats.length) return;
    const topIds = adaptedChats.slice(0, 2).map(c => c.id).join(',');
    if (prefetchTopRef.current === topIds) return;
    prefetchTopRef.current = topIds;
    adaptedChats.slice(0, 2).forEach(chat => prefetchChat(chat));
  }, [adaptedChats, prefetchChat]);

  // Stub para compatibilidade com verifyChats (não implementado para Instagram agora)
  const verifyChats = async () => {
    return null;
  };

  // setSelectedChat com seed imediato: popula o cache com lastMessage antes da query rodar.
  // Se o cache já tem dados frescos (prefetch/hover), não sobrescreve.
  const setSelectedChatWithSeed = useCallback((chat: any) => {
    const chatId = chat.chat_id ? String(chat.chat_id) : null;
    const instId = chat.instance_id ? String(chat.instance_id) : null;
    const origem = chat.phone ?? chat.origem_contato ?? null;

    if (chatId && instId && (chat.lastMessage || chat.full?.lastMessage)) {
      const qKey = ["chat-messages-instagram-direct", chatId, instId, origem ? String(origem) : null, false];
      const existing = queryClient.getQueryData(qKey);
      // Só faz seed se o cache estiver vazio (não interfere com prefetch)
      if (!existing || (Array.isArray(existing) && existing.length === 0)) {
        const lm = chat.lastMessage ?? chat.full?.lastMessage;
        if (lm) {
          queryClient.setQueryData(qKey, [{ ...lm, id: lm.id ?? lm.message_id }]);
        }
      }
    }
    setSelectedChat(chat);
  }, [queryClient, setSelectedChat]);

  return {
    instances: instances || [],
    instancesLoading,
    chats: adaptedChats,
    chatsLoading,
    selectedInstance,
    setSelectedInstance,
    selectedChat,
    setSelectedChat: setSelectedChatWithSeed,
    selectedChatMessages: (selectedChatMessages || []) as ChatMessageInstagram[],
    isMessagesLoading: isMessagesPending,
    isMessagesFetching,
    searchQuery,
    setSearchQuery,
    showAllMessages, // Compatibilidade com Chats.tsx
    setShowAllMessages, // Compatibilidade com Chats.tsx
    refetchChats: async () => {
      // Invalidar cache e forçar refetch
      await queryClient.invalidateQueries({ queryKey: chatsQueryKey });
      return refetchChats();
    },
    // Invalidar e refazer fetch das mensagens do chat (para mensagem de atribuição aparecer no histórico)
    refetchMessages: async () => {
      const { chatId, instanceId, origemContato } = selectedChatIds;
      if (instanceId) {
        const key = ["chat-messages-instagram-direct", chatId, instanceId, origemContato, showAllMessages] as const;
        await queryClient.invalidateQueries({ queryKey: key });
        await queryClient.refetchQueries({ queryKey: key });
      }
      await queryClient.invalidateQueries({
        queryKey: chatsQueryKey,
      });
      await refetchChats();
    },
    insertMessage: insertMessageMutation.mutate,
    insertMessageAsync: insertMessageMutation.mutateAsync,
    insertMessageLoading: insertMessageMutation.isPending,
    markAsViewed: markAsViewedMutation.mutate,
    closeChat: closeChatMutation.mutate,
    closeChatLoading: closeChatMutation.isPending,
    verifyChats, // Stub para compatibilidade
    prefetchChat,
  };
}
